/**
 * Bits shared by the teacher pages: who is logged in, the login form,
 * labels for session phases, the participant list.
 */
import type { PublicHealthResponse, SessionPhase } from '@schulspiele/protocol';

import { api, ApiError } from '@shared/api';
import { esc, qs } from '@shared/dom';
import { t } from '@shared/i18n/de';
import { workLayout } from '../layout';

export interface Teacher {
  id: string;
  username: string;
  displayName: string;
  isAdmin: boolean;
}

export async function currentTeacher(): Promise<Teacher | null> {
  const res = await api<{ teacher: Teacher | null }>('/api/me/optional');
  return res.teacher;
}

/**
 * Render the login form; calls `onSuccess` after a successful login so the
 * caller can render the page the teacher originally wanted.
 */
export function renderLogin(mount: HTMLElement, onSuccess: () => void): void {
  const main = workLayout(
    mount,
    `<div class="card login-card">
       <h1>${t('login.title')}</h1>
       <form class="stack--tight" data-login>
         <label class="field">${t('login.username')}<input name="username" autocomplete="username" required autocapitalize="off" /></label>
         <label class="field">${t('login.password')}<input name="password" type="password" autocomplete="current-password" required /></label>
         <p class="muted" data-demo hidden>${t('login.demo')}</p>
         <p class="error" data-error role="alert"></p>
         <button class="btn btn--big" type="submit">${t('login.submit')}</button>
       </form>
       <p class="muted">${t('login.noAccount')}</p>
     </div>`,
  );
  const form = qs<HTMLFormElement>(main, '[data-login]');
  qs<HTMLInputElement>(form, 'input[name="username"]').focus();
  // A demo server (DEMO=1) says so, with its throwaway login.
  void api<PublicHealthResponse>('/health')
    .then((h) => {
      if (h.demo) qs(form, '[data-demo]').hidden = false;
    })
    .catch(() => undefined);
  const errorEl = qs(main, '[data-error]');
  const submit = qs<HTMLButtonElement>(form, 'button[type="submit"]');
  /**
   * Too many logins at the same moment (a whole staff room after the bell)
   * is no mistake of the teacher's: wait the few seconds and try again by
   * itself (U19). Only the real limit for wrong passwords asks to wait.
   */
  const send = (body: unknown, retries: number): void => {
    void api('/api/login', { method: 'POST', body })
      .then(() => {
        onSuccess();
      })
      .catch((err: unknown) => {
        const wait =
          err instanceof ApiError && err.status === 429
            ? ((err.body as { retryAfterSec?: number } | null)?.retryAfterSec ?? 60)
            : null;
        if (wait !== null && wait <= 5 && retries > 0) {
          errorEl.textContent = t('login.busy');
          window.setTimeout(() => {
            send(body, retries - 1);
          }, wait * 1000);
          return;
        }
        submit.disabled = false;
        errorEl.textContent =
          wait === null
            ? t('login.failed')
            : wait <= 5
              ? t('login.busyLater')
              : t('login.throttled', { minutes: String(Math.max(1, Math.ceil(wait / 60))) });
      });
  };
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const data = new FormData(form);
    submit.disabled = true;
    errorEl.textContent = '';
    send({ username: data.get('username'), password: data.get('password') }, 5);
  });
}

export function phaseLabel(phase: SessionPhase): string {
  return phase === 'lobby'
    ? t('session.lobby')
    : phase === 'running'
      ? t('session.running')
      : t('session.ended');
}

/** The phase as a pill; a running game whose module is done reads "Zu Ende". */
export function phasePill(phase: SessionPhase, gameOver = false): string {
  if (gameOver && phase === 'running')
    return `<span class="status-pill status-pill--ended">${esc(t('session.gameOverPill'))}</span>`;
  return `<span class="status-pill status-pill--${phase}">${esc(phaseLabel(phase))}</span>`;
}

export function formatDate(ms: number): string {
  return new Date(ms).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}
